import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Card } from '../components/Card';
import { Pill } from '../components/Pill';
import { Icon } from '../components/Icons';
import { ThemeToggle } from '../components/ThemeToggle';
import { PayoutMethodModal } from '../components/PayoutMethodModal';
import { Field, FormError, inputStyle } from '../components/form';
import { btnGhost, btnPrimary } from '../components/buttons';
import { authApi } from '../api/auth';
import { creatorApi } from '../api/creator';
import { useAuthStore } from '../auth/store';
import type { ApiError } from '../api/client';

export function SettingsScreen() {
  const creator = useAuthStore((s) => s.creator);
  if (!creator) return null;

  return (
    <div style={{ padding: '24px 28px 60px', display: 'grid', gap: 18, maxWidth: 820 }}>
      <ProfileCard />
      <AppearanceCard />
      <PayoutMethodCard />
      <ChangePasswordCard />
    </div>
  );
}

function SectionHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div>
      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 16 }}>{title}</div>
      {subtitle && <div style={{ color: 'var(--ink-3)', fontSize: 12, marginTop: 2 }}>{subtitle}</div>}
    </div>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px dashed var(--line)', paddingTop: 10 }}>
      <span style={{ color: 'var(--ink-3)', fontSize: 13 }}>{k}</span>
      <span style={{ fontSize: 13, fontWeight: 500 }}>{v}</span>
    </div>
  );
}

function ProfileCard() {
  const creator = useAuthStore((s) => s.creator)!;
  return (
    <Card padding={20} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            background: 'var(--brand)',
            color: 'var(--bg-elev)',
            display: 'grid',
            placeItems: 'center',
            fontFamily: 'var(--font-display)',
            fontWeight: 700,
            fontSize: 16,
          }}
        >
          {creator.avatarInitials}
        </div>
        <div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 600 }}>{creator.name}</div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', fontFamily: 'var(--font-mono)' }}>@{creator.handle}</div>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Row k="Email" v={<span className="mono">{creator.email}</span>} />
        <Row k="Commission" v={`${(creator.commissionRate * 100).toFixed(0)}% per renewal`} />
        <Row k="Country" v={creator.country ?? '—'} />
        <Row k="Code limits" v={`${creator.primaryLimit} primary · ${creator.sideLimit} side`} />
      </div>
      <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
        Profile details and commission rate are managed by the Snapmon team. Contact support to change them.
      </div>
    </Card>
  );
}

function AppearanceCard() {
  return (
    <Card padding={20} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14 }}>
      <SectionHeader title="Appearance" subtitle="Switch between light and dark mode" />
      <ThemeToggle />
    </Card>
  );
}

function PayoutMethodCard() {
  const method = useQuery({ queryKey: ['creator', 'payout-method'], queryFn: creatorApi.payoutMethod });
  const m = method.data;
  const [open, setOpen] = useState(false);
  return (
    <Card padding={20} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 14 }}>
        <SectionHeader title="Payout method" subtitle="Where your monthly earnings are sent" />
        {m?.verified ? <Pill tone="good">Verified</Pill> : m ? <Pill tone="warn">Pending verification</Pill> : <Pill tone="neutral">None</Pill>}
      </div>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 14, color: 'var(--ink-2)' }}>
        {m?.type === 'bank'
          ? `${m.bankName ?? 'Bank'} · ••${m.bankLast4 ?? '----'}`
          : m?.type === 'paypal'
            ? m.paypalEmail
            : 'No method on file'}
      </div>
      <div>
        <button style={btnGhost} onClick={() => setOpen(true)}>{m ? 'Update method' : 'Add method'}</button>
      </div>
      <PayoutMethodModal open={open} onClose={() => setOpen(false)} current={m} />
    </Card>
  );
}

const PwSchema = z
  .object({
    currentPassword: z.string().min(1, 'Required'),
    newPassword: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string(),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords do not match',
  });
type PwValues = z.infer<typeof PwSchema>;

function ChangePasswordCard() {
  const setSession = useAuthStore((s) => s.setSession);
  const [formError, setFormError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const { register, handleSubmit, reset, formState: { errors } } = useForm<PwValues>({
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  const mutation = useMutation({
    mutationFn: (v: PwValues) => authApi.changePassword(v.currentPassword, v.newPassword),
    onSuccess: (res) => {
      setSession(res.token, res.creator, false);
      reset();
      setDone(true);
    },
    onError: (err: ApiError) => setFormError(err?.message ?? 'Password change failed'),
  });

  const onSubmit = (vals: PwValues) => {
    const parsed = PwSchema.safeParse(vals);
    if (!parsed.success) {
      setFormError(parsed.error.errors[0]?.message ?? 'Invalid input');
      return;
    }
    setFormError(null);
    setDone(false);
    mutation.mutate(parsed.data);
  };

  return (
    <Card padding={20} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <SectionHeader title="Password" subtitle="Update the password you use to sign in" />
      <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 420 }}>
        <Field label="Current password" error={errors.currentPassword?.message}>
          <input {...register('currentPassword')} type="password" placeholder="••••••••" style={{ ...inputStyle, fontFamily: 'var(--font-mono)', letterSpacing: '0.18em' }} />
        </Field>
        <Field label="New password" error={errors.newPassword?.message}>
          <input {...register('newPassword')} type="password" placeholder="••••••••" style={{ ...inputStyle, fontFamily: 'var(--font-mono)', letterSpacing: '0.18em' }} />
        </Field>
        <Field label="Confirm new password" error={errors.confirmPassword?.message}>
          <input {...register('confirmPassword')} type="password" placeholder="••••••••" style={{ ...inputStyle, fontFamily: 'var(--font-mono)', letterSpacing: '0.18em' }} />
        </Field>
        <FormError message={formError} />
        {done && (
          <div style={{ fontSize: 12.5, color: 'var(--good)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Icon.Check /> Password updated.
          </div>
        )}
        <div>
          <button type="submit" style={btnPrimary} disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving…' : 'Save new password'}
          </button>
        </div>
      </form>
    </Card>
  );
}
